import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useCallback, useMemo, useState } from "react";
import { SwiperSlide } from "swiper/react";
import RoomInfoModal from "@/components/pages/hotel/detail/RoomInfoModal";
import Swiper from "@/components/swiper";
import { Button } from "@/components/ui/Button";
import { PATHS } from "@/constants/pathnames";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useLoading } from "@/contexts/LoadingContext";
import { useModal } from "@/contexts/ModalContext";
import { usePriceFormatter } from "@/hooks/usePriceFormatter";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { getPathname } from "@/i18n/navigation";
import { hotelReservationService } from "@/services/hotelServices/reservation";
import { getClientSessionHeaders } from "@/utils/clientSessionHeadersHelper";
import { formatCurrency } from "@/utils/currencyFormatter";

const PriceDisplay = ({
  original,
  discounted,
  wrapperClassName = "",
  isMainContainer = false,
}) => {
  const { formatPrice } = usePriceFormatter();
  const showOriginal = original !== discounted && original > discounted;
  const DECIMALS = 0;

  return (
    <div className={clsx("flex flex-row items-center gap-2", wrapperClassName)}>
      {showOriginal && discounted && (
        <span
          className={clsx(
            "text-foreground-muted text-[11px] leading-[16px] font-normal break-words line-through",
            isMainContainer ? "text-sm! leading-[19px]" : "",
          )}
        >
          {formatPrice(original, undefined, DECIMALS)}
        </span>
      )}

      <span
        className={clsx(
          "text-foreground text-base leading-21 font-semibold break-words",
          isMainContainer ? "text-[23px] leading-[30px]" : "",
        )}
      >
        {formatPrice(discounted || original, undefined, DECIMALS)}
      </span>
    </div>
  );
};

// Board kodlarını translation key'lerine map eden fonksiyon
const getBoardTranslationKey = (boardCode) => {
  if (!boardCode) return null;

  const boardMapping = {
    RO: "IsRoomOnly",
    BB: "IsBreakfastIncluded",
    HB: "IsHalfBoard",
    FB: "IsFullBoard",
    AI: "IsAllInclusive",
    UAI: "IsUltraAllInclusive",
    "FL+FD": "IsLunchAndDinner",
    AIWA: "IsAlcoholFreeAllInclusive",
    AAI: "IsALaCarteAllInclusive",
    BBS: "IsTraditionalTurkishBreakfast",
  };

  return boardMapping[boardCode] || null;
};

const RoomCard = (props) => {
  const t = useTranslationDetector("hotel");
  const { currency } = useCurrency();
  const { show, hide } = useLoading();
  const locale = useLocale();
  const [selectedOption, setSelectedOption] = useState(props.options[0]);
  const [showAllOptions, setShowAllOptions] = useState(false); // Tüm seçenekleri göster/gizle
  const [isBooking, setIsBooking] = useState(false);
  const roomInfoModal = useModal(`room-info-modal-${props.id}`);
  const cancellationPolicyModal = useModal("cancellation-policy-modal");

  // Konuk bilgileri ve gece sayısı hesaplama
  const guests = props.guests || { adult: 1, child: 0, childrenAges: [] };
  const checkinDate = props.checkinDate;
  const checkoutDate = props.checkoutDate;

  // Gece sayısını hesapla
  const nights = useMemo(() => {
    if (!checkinDate || !checkoutDate) return 0;
    const checkin = new Date(checkinDate);
    const checkout = new Date(checkoutDate);
    const diffTime = checkout - checkin;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  }, [checkinDate, checkoutDate]);

  // Konuk bilgisi formatla - useMemo kaldırıldı, her render'da güncel değerleri kullan
  const adultCount = Number(guests?.adult) || 0;
  const childCount = Number(guests?.child) || 0;

  const parts = [];
  if (adultCount > 0 && !isNaN(adultCount)) {
    parts.push(`${adultCount} ${t("adult")}`);
  }
  if (childCount > 0 && !isNaN(childCount)) {
    parts.push(`${childCount} ${t("child")}`);
  }
  const guestsText = parts.length > 0 ? parts.join(", ") : "";

  // Landing sayfası kontrolü: Eğer options yoksa veya tüm fiyatlar 0 ise, landing sayfası modunda göster
  const hasValidPricing =
    props.options &&
    props.options.length > 0 &&
    props.options.some((opt) => opt.price > 0 || opt.discountedPrice > 0);
  const isLandingPage = !hasValidPricing;

  const handleShowDetailModal = () => {
    roomInfoModal.open();
  };

  const renderImages = useCallback(
    ({ containerClassName = "", swiperOptions = {}, imageClassName = "" }) => {
      return (
        <div
          className={clsx(
            "relative h-[200px] w-full overflow-hidden rounded-[10px] xl:max-w-[300px] xl:min-w-[300px]",
            containerClassName,
          )}
        >
          <div
            className="relative w-full overflow-hidden rounded-none md:rounded-lg"
            onClick={handleShowDetailModal}
          >
            <Swiper
              customClass="mobile-hotel-gallery-swiper"
              swiperOptions={{
                ...swiperOptions,
                loop: true,
                slidesPerView: 1,
                spaceBetween: 12,
                pagination: {
                  dynamicBullets: true,
                  dynamicMainBullets: 2,
                  hideOnClick: false,
                  bulletClass: "room-card-bullet",
                  bulletActiveClass: "room-card-bullet-active",
                  containerClass:
                    "absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2",
                },
                navigation: true,
                navigationPrevButtonClass:
                  "absolute left-2 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-foreground-muted focus:outline-none hidden sm:flex",
                navigationNextButtonClass:
                  "absolute right-2 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white text-foreground-muted focus:outline-none hidden sm:flex",
                navigationPrevButtonIcon: (
                  <FontAwesomeIcon
                    className="h-4! w-4!"
                    icon={byPrefixAndName.far["angle-left"]}
                  />
                ),
                navigationNextButtonIcon: (
                  <FontAwesomeIcon
                    className="h-4! w-4!"
                    icon={byPrefixAndName.far["angle-right"]}
                  />
                ),
              }}
            >
              {props.images.map((image, index) => (
                <SwiperSlide key={image.id ?? index}>
                  <div
                    className={clsx(
                      "relative h-[200px] w-full overflow-hidden rounded-none md:rounded-lg",
                      imageClassName,
                    )}
                  >
                    <Image
                      src={image.src}
                      alt={image.alt}
                      fill
                      unoptimized
                      sizes="100vw"
                      className="object-cover"
                      priority={index === 0}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className="absolute right-4 bottom-4 z-1 flex items-center gap-1 rounded-4xl bg-[#1D1E2066] px-2 py-1.5 text-white">
            <FontAwesomeIcon
              className="h-4! w-4!"
              icon={byPrefixAndName.far["image"]}
            />

            <span className="text-sm">{props.images.length}</span>
          </div>
        </div>
      );
    },
    [props.images],
  );

  const renderOptions = useCallback(
    (
      className = "",
      popupContainerClassName = "",
      modalContainerClassName = "",
    ) => {
      // İlk 3 seçeneği göster, eğer showAllOptions true ise tümünü göster
      const displayedOptions = showAllOptions
        ? props.options
        : (props.options || []).slice(0, 2);
      const hasMoreOptions = (props.options || []).length > 2;

      return (
        <div className={clsx("flex flex-col gap-4", modalContainerClassName)}>
          <div
            className={clsx("text-foreground text-base font-medium", className)}
          >
            {t("accommodationOptions")}
          </div>

          {displayedOptions &&
            displayedOptions.map((option) => (
              <button
                key={option.id}
                className={clsx(
                  "flex cursor-pointer flex-col gap-3 rounded-[10px] border border-solid",
                  option.id === selectedOption.id
                    ? "border-primary"
                    : "border-[#D0D0DC]",
                  option.discountText ? "px-4 py-[12.5px]" : "p-4",
                  popupContainerClassName,
                )}
                onClick={() => {
                  setSelectedOption(option);
                }}
              >
                <div className="flex flex-row items-center justify-between gap-3">
                  <div className="flex flex-row items-center gap-3">
                    <span
                      className={clsx(
                        "flex h-5 w-5 rounded-full border border-solid",
                        option.id === selectedOption.id
                          ? "border-primary border-6"
                          : "border-[#D0D0DC]",
                      )}
                    />
                    <div className="flex flex-col items-start">
                      <span className="text-start font-medium">
                        {(() => {
                          // Board kodunu translation key'ine çevir
                          const translationKey = getBoardTranslationKey(
                            option.label,
                          );
                          if (translationKey) {
                            // Translation key varsa, hotel namespace'inden al
                            return t(translationKey) || option.label;
                          }
                          // Translation key yoksa, orijinal label'ı göster
                          return option.label;
                        })()}
                      </span>

                      <div className="flex items-center gap-1 text-xs">
                        {option.refundable ? (
                          <div className="text-(--color-teal)">
                            {t("freeCancellation")}
                          </div>
                        ) : (
                          <div>{t("noCancellation")}</div>
                        )}

                        <div
                          onClick={() => {
                            const policy = option.cancellationPolicy || {};
                            const isRefundable =
                              policy.IsRefundable || option.refundable || false;
                            const providerId = props.providerId;

                            // ProviderId'ye göre cancellation verilerini seç
                            // ProviderId === 6 (ETS otelleri) → CancellationSchedules kullan
                            // ProviderId === 7 → CancellationPenalties kullan
                            const useSchedules = providerId === 6;
                            const penalties = useSchedules
                              ? {}
                              : policy.CancellationPenalties || {};
                            const schedules = useSchedules
                              ? policy.CancellationSchedules || []
                              : [];

                            // Deadline formatla (eğer geçerli bir tarih ise)
                            const formatDeadline = (deadline) => {
                              if (
                                !deadline ||
                                deadline === "0001-01-01T00:00:00"
                              ) {
                                return null;
                              }
                              try {
                                const date = new Date(deadline);
                                if (isNaN(date.getTime())) return null;
                                return date.toLocaleDateString(
                                  locale || "tr-TR",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                );
                              } catch {
                                return null;
                              }
                            };

                            // ETS için tarih formatla (DD.MM.YYYY HH:mm formatında)
                            const formatDateForETS = (dateString) => {
                              if (
                                !dateString ||
                                dateString === "0001-01-01T00:00:00"
                              ) {
                                return null;
                              }
                              try {
                                const date = new Date(dateString);
                                if (isNaN(date.getTime())) return null;
                                const day = String(date.getDate()).padStart(
                                  2,
                                  "0",
                                );
                                const month = String(
                                  date.getMonth() + 1,
                                ).padStart(2, "0");
                                const year = date.getFullYear();
                                const hours = String(date.getHours()).padStart(
                                  2,
                                  "0",
                                );
                                const minutes = String(
                                  date.getMinutes(),
                                ).padStart(2, "0");
                                return `${day}.${month}.${year} ${hours}:${minutes}`;
                              } catch {
                                return null;
                              }
                            };

                            // Amount formatla (CancellationSchedules için)
                            const formatAmount = (amount) => {
                              if (!amount || amount === 0) return null;
                              return formatCurrency(amount, currency, {
                                showSymbol: true,
                                showCode: false,
                                round: false,
                              });
                            };

                            const deadline = useSchedules
                              ? null
                              : formatDeadline(penalties.Deadline);

                            cancellationPolicyModal.open({
                              showCancel: false,
                              title: t("cancellationPolicyModalTitle"),
                              message: (
                                <div className="mb-3">
                                  {/* ProviderId === 6 (ETS) için CancellationSchedules göster */}
                                  {useSchedules && schedules.length > 0 && (
                                    <div className="">
                                      <div className="space-y-3">
                                        {schedules.map((schedule, index) => {
                                          const currentDate = formatDateForETS(
                                            schedule.Date,
                                          );
                                          const previousSchedule =
                                            index > 0
                                              ? schedules[index - 1]
                                              : null;
                                          let startDate = previousSchedule
                                            ? formatDateForETS(
                                                previousSchedule.Date,
                                              )
                                            : null;

                                          // İlk schedule için başlangıç tarihi yoksa, şu anki tarihi kullan
                                          if (!startDate && index === 0) {
                                            startDate = formatDateForETS(
                                              new Date().toISOString(),
                                            );
                                          }

                                          const scheduleAmount =
                                            schedule.Amount || 0;
                                          const isFree = scheduleAmount === 0;

                                          // Tarih aralığı oluştur
                                          let dateRange = "";
                                          if (startDate && currentDate) {
                                            dateRange = `${startDate} - ${currentDate}`;
                                          } else if (currentDate) {
                                            dateRange = currentDate;
                                          }

                                          return (
                                            <div
                                              key={index}
                                              className="text-sm leading-6"
                                            >
                                              {dateRange && (
                                                <div className="text-foreground text-base leading-21">
                                                  <span className="font-medium">
                                                    {dateRange}
                                                  </span>{" "}
                                                  {t("betweenDates")}{" "}
                                                  {isFree ? (
                                                    <span>
                                                      {t("freeRefundable")}
                                                    </span>
                                                  ) : (
                                                    <span>
                                                      {t(
                                                        "cancellationDeduction",
                                                        {
                                                          amount:
                                                            formatAmount(
                                                              scheduleAmount,
                                                            ),
                                                        },
                                                      )}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <div className="text-foreground-muted mt-5 text-base leading-21">
                                        {t("cancellationLocalTime")}
                                      </div>
                                    </div>
                                  )}

                                  {/* ProviderId === 7 için CancellationPenalties göster */}
                                  {!useSchedules && (
                                    <>
                                      {deadline && (
                                        <>
                                          <div className="text-foreground text-base leading-21">
                                            {t("cancellationDeadlineMessage")}
                                          </div>
                                          <div className="mt-3 flex items-center gap-1 text-base">
                                            <div className="font-medium">
                                              {t("lastCancellationDate")}:
                                            </div>
                                            <div>{deadline}</div>
                                          </div>
                                        </>
                                      )}

                                      {penalties.CancellationPrice > 0 && (
                                        <div className="mt-3 flex items-center gap-1 text-base">
                                          <div className="font-medium">
                                            {t("deductionAmount")}:
                                          </div>
                                          <div>
                                            {formatCurrency(
                                              penalties.CancellationPrice,
                                              currency,
                                              {
                                                showSymbol: true,
                                                showCode: false,
                                                round: false,
                                              },
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {/* Eğer hiç detay yoksa genel mesaj göster */}
                                  {((useSchedules && schedules.length === 0) ||
                                    (!useSchedules &&
                                      !deadline &&
                                      penalties.HoursBeforeDeadline === 0 &&
                                      penalties.CancellationPrice === 0)) && (
                                    <div>
                                      <div className="text-lg font-semibold">
                                        {t("cancellationMessageNonRefundable")}
                                      </div>
                                      <div className="text-foreground mt-3 text-base leading-21">
                                        {isRefundable
                                          ? t("cancellableDescription", {
                                              defaultValue:
                                                "Bu rezervasyon iptal edilebilir. Detaylı bilgi için otel ile iletişime geçin.",
                                            })
                                          : t("nonRefundableDescription", {
                                              defaultValue:
                                                "Rezervasyonun iptali veya kullanılmaması durumunda rezervasyon tutarı iade edilmeyecektir.",
                                            })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ),
                              isBottomSheet: true,
                              titleCentered: true,
                              showCloseButton: true,
                              contentClassName:
                                "flex-1 overflow-y-auto p-4 md:px-6 md:pt-4 md:pb-5 [&_button]:hidden",
                            });
                          }}
                        >
                          <FontAwesomeIcon
                            className="h-4 w-4"
                            color={option.refundable ? "#05807B" : undefined}
                            icon={byPrefixAndName.far["circle-info"]}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {option.discountText && (
                      <span className="rounded-[4px] bg-[var(--color-promo-weak)] px-[6px] py-[3px] text-xs font-medium text-(--color-promo)">
                        {option.discountText}
                      </span>
                    )}

                    <PriceDisplay
                      original={option.price}
                      discounted={option.discountedPrice}
                    />
                  </div>
                </div>

                {option.membershipDiscountText && (
                  <div className="flex justify-end">
                    <div className="flex items-center gap-[6px] bg-[var(--color-background-teal-weaker)] px-[10px] py-[6px] text-[13px] font-medium text-(--color-teal-strong)">
                      <FontAwesomeIcon
                        className="h-[14px] w-[14px]"
                        icon={byPrefixAndName.far["user-check"]}
                      />

                      {option.membershipDiscountText}
                    </div>
                  </div>
                )}
              </button>
            ))}

          {/* Eğer 3'ten fazla seçenek varsa ve tümü gösterilmiyorsa "Daha fazla göster" butonu */}
          {hasMoreOptions && !showAllOptions && (
            <button
              onClick={() => setShowAllOptions(true)}
              className="text-primary flex cursor-pointer items-center justify-start gap-1 text-sm font-medium transition-opacity hover:opacity-80"
            >
              <span>{t("showMoreOptions")}</span>
              <FontAwesomeIcon
                icon={byPrefixAndName.far["chevron-down"]}
                className="text-sm"
              />
            </button>
          )}

          {/* Eğer tüm seçenekler gösteriliyorsa "Daha az göster" butonu */}
          {hasMoreOptions && showAllOptions && (
            <button
              onClick={() => setShowAllOptions(false)}
              className="text-primary flex items-center justify-start gap-1 text-sm font-medium transition-opacity hover:opacity-80"
            >
              <span>{t("showLessOptions")}</span>
              <FontAwesomeIcon
                icon={byPrefixAndName.far["chevron-up"]}
                className="text-sm"
              />
            </button>
          )}
        </div>
      );
    },
    [selectedOption, props.options, showAllOptions, t, currency],
  );

  const renderBookButton = useCallback(
    ({ isResponsive = true, closeModal } = {}) => {
      // Landing sayfası modunda: Sadece "Fiyat Göster" butonu
      if (isLandingPage) {
        return (
          <div className="flex w-full flex-row items-end justify-end xl:w-auto">
            <Button
              onClick={() => {
                closeModal?.();
                props.onShowPriceClick?.();
              }}
            >
              {t("showPrice")}
            </Button>
          </div>
        );
      }
      // Detay sayfası modunda: Fiyat + "Odayı Ayır" butonu
      return (
        <div
          className={clsx(
            "flex flex-row items-center justify-end gap-3",
            isResponsive ? "xl:flex-col xl:items-end" : "",
          )}
        >
          <div className="flex flex-col items-end">
            {selectedOption.discountText && (
              <span className="rounded-[4px] bg-[var(--color-promo-weak)] px-[6px] py-[3px] text-xs font-medium text-(--color-promo)">
                {selectedOption.discountText}
              </span>
            )}

            <PriceDisplay
              isMainContainer={true}
              wrapperClassName="mt-1"
              original={selectedOption.price}
              discounted={selectedOption.discountedPrice}
            />

            {nights > 0 && guestsText && (
              <div className="text-foreground-muted text-end text-sm sm:mt-0.5">
                {nights} {t("night")}, {guestsText}
              </div>
            )}
          </div>

          <Button
            className="h-11 w-[122px]"
            onClick={async () => {
              closeModal?.();
              if (isBooking) return; // Zaten booking işlemi devam ediyorsa tekrar tıklamayı engelle

              // add_to_cart analytics: Seç (Odayı Ayır) tıklanınca tetikle
              const accommodationLabel = (() => {
                const translationKey = getBoardTranslationKey(
                  selectedOption?.label,
                );
                if (translationKey)
                  return t(translationKey) || selectedOption?.label;
                return selectedOption?.label ?? "";
              })();
              props.onAddToCart?.(
                { id: props.id, label: props.label },
                selectedOption,
                accommodationLabel,
              );

              // Gerekli verileri kontrol et
              if (
                !props.reference ||
                !props.normalSearchReference ||
                !selectedOption?.id ||
                !props.id
              ) {
                console.error(
                  "❌ [ROOM CARD] Missing required data for reservation:",
                  {
                    reference: props.reference,
                    normalSearchReference: props.normalSearchReference,
                    priceKey: selectedOption?.id,
                    roomId: props.id,
                  },
                );
                return;
              }

              setIsBooking(true);
              show();

              try {
                // Session header'larını al
                const sessionHeaders = getClientSessionHeaders();

                // Rezervasyon servisini çağır
                const response = await hotelReservationService.getReservation(
                  {
                    reference: props.reference,
                    normalSearchReference: props.normalSearchReference,
                    roomId: props.id,
                    priceKey: selectedOption.id,
                    preferences: {
                      language: locale,
                      currency,
                      nationality: "TR", // TODO: Global'den alınacak
                    },
                  },
                  sessionHeaders,
                );

                // BasketId'yi al
                const basketId = response?.BasketId;

                if (basketId) {
                  // Rezervasyon sayfasına yönlendir (full reload - footer görünürlüğü için)
                  const reservationPath = PATHS.HOTEL_RESERVATION.PATH.replace(
                    "[reservationId]",
                    basketId.toString(),
                  );
                  const localizedPath = getPathname({
                    href: reservationPath,
                    locale,
                  });
                  window.location.href = localizedPath;
                } else {
                  console.error(
                    "❌ [ROOM CARD] BasketId not found in response",
                  );
                  hide();
                  // TODO: Hata mesajı göster
                }
              } catch (error) {
                console.error("❌ [ROOM CARD] Reservation error:", error);
                hide();
                // TODO: Hata mesajı göster
              } finally {
                setIsBooking(false);
              }
            }}
            disabled={isBooking}
          >
            {isBooking ? t("booking") : t("bookRoom")}
          </Button>
        </div>
      );
    },
    [
      selectedOption,
      isLandingPage,
      t,
      guestsText,
      nights,
      show,
      props.onAddToCart,
      props.id,
      props.label,
    ],
  );

  return (
    <>
      <div
        id={`room_${props.id}`}
        className="flex flex-col gap-4 rounded-[10px] bg-white p-4 text-nowrap shadow-lg sm:p-6 xl:flex-row xl:gap-[80px] xl:shadow-md"
      >
        <div
          className="flex w-full cursor-pointer flex-col gap-3 xl:flex-row xl:gap-6"
          onClick={handleShowDetailModal}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleShowDetailModal();
            }
          }}
        >
          {renderImages({ containerClassName: "", swiperNavigation: {} })}

          <div className="flex flex-col gap-4 break-words whitespace-normal xl:w-[221px]">
            <div className="text-foreground text-lg font-semibold">
              {props.label}
            </div>
            <div className="flex flex-row flex-wrap gap-4">
              {props.features.slice(0, 3).map((feature, i) => (
                <div className="flex items-start gap-[6px]" key={i}>
                  <FontAwesomeIcon
                    className="h-[18px]! w-[18px]!"
                    icon={feature.icon}
                  />
                  <span className="text-sm leading-19">{feature.label}</span>
                </div>
              ))}
            </div>

            <button
              className="text-primary hidden flex-row items-center gap-1 md:flex"
              onClick={(e) => {
                e.stopPropagation();
                handleShowDetailModal();
              }}
            >
              <span className="cursor-pointer text-sm leading-21 font-medium md:text-base">
                {t("allRoomFeatures")}
              </span>
            </button>
          </div>
        </div>
        <div
          className={clsx(
            "flex flex-col gap-4",
            isLandingPage
              ? "xl:w-full xl:flex-row xl:justify-end"
              : "xl:w-full xl:flex-row xl:justify-between",
          )}
        >
          {/* Landing sayfası modunda "Konaklama seçenekleri" bölümünü gösterme */}
          {!isLandingPage && renderOptions("", "xl:w-[322px]")}

          {renderBookButton({ isResponsive: true })}
        </div>
      </div>

      <RoomInfoModal
        renderImages={renderImages}
        renderOptions={renderOptions}
        renderBookButton={renderBookButton}
        roomDetail={props}
        isLandingPage={isLandingPage}
        closeModal={roomInfoModal.close}
      />
    </>
  );
};

export default RoomCard;
